import PropTypes from "prop-types";
import { useMemo } from "react";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormMultiSelect } from "components/BaseForm";
import BaseForm from "components/BaseForm/BaseForm";
import {
  formatCount,
  getSessionNames,
  registrantShape,
} from "routes/swoogo/registrantSessions";
import useRemoveSessionRegistrants from "routes/swoogo/useRemoveSessionRegistrants";

const schema = z.object({
  session_ids: z.array(z.number()).min(1, "Select at least one session"),
});

const getSessionOptions = registrants =>
  [...getSessionNames(registrants)]
    .map(([id, name]) => ({ value: id, text: name }))
    .sort((a, b) => a.text.localeCompare(b.text));

/** Removes the selected registrants from the chosen sessions in Swoogo. */
const RemoveSessionRegistrantsDialog = ({
  registrants,
  onOpenChange,
  onSuccess,
}) => {
  const removeMutation = useRemoveSessionRegistrants(registrants, () => {
    onOpenChange(false);
    onSuccess?.();
  });
  const options = useMemo(() => getSessionOptions(registrants), [registrants]);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="tw:sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Remove from Sessions</DialogTitle>
          <DialogDescription>
            Choose the sessions to remove the{" "}
            {formatCount(registrants.length, "selected registrant")} from. The
            change is made in Swoogo and takes some time to show.{" "}
            {registrants.length === 1
              ? "The registrant stays registered for the event."
              : "The registrants stay registered for the event."}
          </DialogDescription>
        </DialogHeader>
        <BaseForm
          schema={schema}
          defaultValues={{ session_ids: [] }}
          onSubmit={values => removeMutation.mutateAsync(values.session_ids)}
          onCancel={() => onOpenChange(false)}
          submitButtonText="Remove"
          submittingButtonText="Removing"
        >
          <FormMultiSelect
            name="session_ids"
            label="Sessions"
            required
            options={options}
            placeholder="Select sessions"
          />
        </BaseForm>
      </DialogContent>
    </Dialog>
  );
};

RemoveSessionRegistrantsDialog.propTypes = {
  registrants: PropTypes.arrayOf(registrantShape).isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default RemoveSessionRegistrantsDialog;
